import {
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle,
  PencilLine,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { usePatientDialog } from '../patients/use-patient-dialog'
import {
  INSURANCE_INFORMATION_MAX_ENTRIES,
  INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS,
} from './assistant-settings-contracts'
import {
  CARRIERS_MAX,
  aliasError,
  carrierError,
  matchesSearch,
  noteError,
  sharedNames,
  type Carrier,
} from './insurance-model'
import panel from './SettingsSection.module.css'
import styles from './AiRulesSettings.module.css'

type Target = { index: number | null }

/**
 * Accepted carriers and insurance notes as scannable lists. Each item opens a
 * dialog; changes go into the insurance draft and are sent with Save insurance.
 */
export function InsuranceLists({
  carriers,
  notes,
  disabled,
  onCarriersChange,
  onNotesChange,
}: {
  carriers: Carrier[]
  notes: string[]
  disabled: boolean
  onCarriersChange: (carriers: Carrier[]) => void
  onNotesChange: (notes: string[]) => void
}) {
  const baseId = useId()
  const [search, setSearch] = useState('')
  const [carrierTarget, setCarrierTarget] = useState<Target | null>(null)
  const [noteTarget, setNoteTarget] = useState<Target | null>(null)
  const shown = useMemo(
    () =>
      carriers
        .map((carrier, index) => ({ carrier, index }))
        .filter(({ carrier }) => matchesSearch(carrier, search)),
    [carriers, search]
  )

  return (
    <>
      <div className={styles.insuranceBlock}>
        <div className={styles.blockHead}>
          <div>
            <h3>
              Accepted insurance{' '}
              <span className={styles.count}>{carriers.length}</span>
            </h3>
            <p>
              Ava uses this list when patients ask which insurance the clinic
              accepts. Other names help her recognize what patients say.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || carriers.length >= CARRIERS_MAX}
            onClick={() => setCarrierTarget({ index: null })}
          >
            <Plus size={16} aria-hidden="true" /> Add carrier
          </Button>
        </div>
        {carriers.length > 6 ? (
          <label className={styles.search}>
            <Search size={15} aria-hidden="true" />
            <span className={panel.srOnly}>Search accepted insurance</span>
            <input
              type="search"
              placeholder="Search carriers"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </label>
        ) : null}
        {carriers.length === 0 ? (
          <p className={styles.emptyRules}>No carriers listed yet.</p>
        ) : shown.length === 0 ? (
          <p className={styles.emptyRules} role="status">
            No carriers match “{search.trim()}”.
          </p>
        ) : (
          <ul
            className={styles.carrierList}
            aria-label="Accepted insurance carriers"
          >
            {shown.map(({ carrier, index }) => (
              <li
                key={`${carrier.name}-${index}`}
                className={styles.carrierRow}
              >
                <span className={styles.carrierText}>
                  <strong>{carrier.name}</strong>
                  {carrier.aliases.length ? (
                    <small>Also: {carrier.aliases.join(', ')}</small>
                  ) : null}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  className={styles.iconEdit}
                  disabled={disabled}
                  aria-label={`Edit ${carrier.name}`}
                  title="Edit"
                  onClick={() => setCarrierTarget({ index })}
                >
                  <PencilLine size={15} aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.insuranceBlock}>
        <div className={styles.blockHead}>
          <div>
            <h3>
              Insurance notes{' '}
              <span className={styles.count}>{notes.length}</span>
            </h3>
            <p>
              Extra guidance, such as plans the clinic is out of network with.
              Notes don’t change which carriers are accepted.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={
              disabled || notes.length >= INSURANCE_INFORMATION_MAX_ENTRIES
            }
            onClick={() => setNoteTarget({ index: null })}
          >
            <Plus size={16} aria-hidden="true" /> Add note
          </Button>
        </div>
        {notes.length === 0 ? (
          <p className={styles.emptyRules}>No insurance notes yet.</p>
        ) : (
          <ul className={styles.noteList} aria-label="Insurance notes">
            {notes.map((note, index) => (
              <li key={`${baseId}-${index}`} className={styles.noteRow}>
                <p title={note}>{note}</p>
                <Button
                  type="button"
                  variant="ghost"
                  className={styles.iconEdit}
                  disabled={disabled}
                  aria-label={`Edit note ${index + 1}`}
                  title="Edit"
                  onClick={() => setNoteTarget({ index })}
                >
                  <PencilLine size={15} aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {carrierTarget ? (
        <CarrierDialog
          carrier={
            carrierTarget.index === null
              ? undefined
              : carriers[carrierTarget.index]
          }
          others={carriers.filter((_, index) => index !== carrierTarget.index)}
          onApply={(carrier) => {
            onCarriersChange(
              carrierTarget.index === null
                ? [...carriers, carrier]
                : carriers.map((item, index) =>
                    index === carrierTarget.index ? carrier : item
                  )
            )
            setCarrierTarget(null)
          }}
          onRemove={
            carrierTarget.index === null
              ? undefined
              : () => {
                  onCarriersChange(
                    carriers.filter((_, index) => index !== carrierTarget.index)
                  )
                  setCarrierTarget(null)
                }
          }
          onClose={() => setCarrierTarget(null)}
        />
      ) : null}
      {noteTarget ? (
        <NoteDialog
          note={noteTarget.index === null ? undefined : notes[noteTarget.index]}
          others={notes.filter((_, index) => index !== noteTarget.index)}
          onApply={(note) => {
            onNotesChange(
              noteTarget.index === null
                ? [...notes, note]
                : notes.map((item, index) =>
                    index === noteTarget.index ? note : item
                  )
            )
            setNoteTarget(null)
          }}
          onRemove={
            noteTarget.index === null
              ? undefined
              : () => {
                  onNotesChange(
                    notes.filter((_, index) => index !== noteTarget.index)
                  )
                  setNoteTarget(null)
                }
          }
          onClose={() => setNoteTarget(null)}
        />
      ) : null}
    </>
  )
}

/** Shared frame: edit, then ask before discarding edits or removing. */
function useAsk() {
  const safeChoice = useRef<HTMLButtonElement>(null)
  const [mode, setMode] = useState<'edit' | 'discard' | 'remove'>('edit')
  function ask(next: 'discard' | 'remove') {
    setMode(next)
    window.setTimeout(() => safeChoice.current?.focus(), 0)
  }
  return { mode, setMode, ask, safeChoice }
}

function DialogFrame({
  eyebrow,
  title,
  onClose,
  children,
  footer,
}: {
  eyebrow: string
  title: string
  onClose: () => void
  children: ReactNode
  footer: ReactNode
}) {
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  usePatientDialog(dialog, { onClose })
  return createPortal(
    <div className={panel.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${panel.dialog} ${styles.insuranceDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className={panel.dialogHeader}>
          <div>
            <span className={panel.eyebrow}>{eyebrow}</span>
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            type="button"
            className={panel.closeButton}
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className={panel.dialogBody}>{children}</div>
        <footer className={panel.dialogFooter}>{footer}</footer>
      </section>
    </div>,
    document.body
  )
}

function RemoveQuestion({ title, lines }: { title: string; lines: string[] }) {
  const id = useId()
  return (
    <div className={styles.confirmPanel} role="group" aria-labelledby={id}>
      <span className={styles.confirmIcon} aria-hidden="true">
        <Trash2 size={18} />
      </span>
      <div>
        <h3 id={id}>{title}</h3>
        <ul>
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function CarrierDialog({
  carrier,
  others,
  onApply,
  onRemove,
  onClose,
}: {
  carrier?: Carrier
  others: Carrier[]
  onApply: (carrier: Carrier) => void
  onRemove?: () => void
  onClose: () => void
}) {
  const baseId = useId()
  const { mode, setMode, ask, safeChoice } = useAsk()
  const [name, setName] = useState(carrier?.name ?? '')
  const [aliases, setAliases] = useState<string[]>(carrier?.aliases ?? [])
  const [alias, setAlias] = useState('')
  const draft = { name: name.trim(), aliases }
  const errors = carrierError(draft, others)
  const pendingAliasError = aliasError(alias, draft)
  const shared = sharedNames(draft, others)
  const changed =
    JSON.stringify(draft) !==
    JSON.stringify({
      name: carrier?.name ?? '',
      aliases: carrier?.aliases ?? [],
    })

  function addAlias() {
    const value = alias.trim()
    if (!value || aliasError(value, draft)) return
    setAliases([...aliases, value])
    setAlias('')
  }

  function requestClose() {
    if (changed) ask('discard')
    else onClose()
  }

  const title = carrier ? `Edit ${carrier.name}` : 'Add carrier'
  return (
    <DialogFrame
      eyebrow="Accepted insurance"
      title={title}
      onClose={() => (mode === 'edit' ? requestClose() : setMode('edit'))}
      footer={
        mode === 'remove' ? (
          <div className={panel.footerActions}>
            <Button
              ref={safeChoice}
              type="button"
              variant="secondary"
              onClick={() => setMode('edit')}
            >
              Keep carrier
            </Button>
            <Button
              type="button"
              className={panel.dangerSolid}
              onClick={onRemove}
            >
              Remove carrier
            </Button>
          </div>
        ) : mode === 'discard' ? (
          <>
            <p className={panel.dialogNote} role="alert">
              Discard your changes to this carrier?
            </p>
            <div className={panel.footerActions}>
              <Button
                ref={safeChoice}
                type="button"
                variant="secondary"
                onClick={() => setMode('edit')}
              >
                Keep editing
              </Button>
              <Button type="button" onClick={onClose}>
                Discard changes
              </Button>
            </div>
          </>
        ) : (
          <>
            {onRemove ? (
              <Button
                type="button"
                variant="secondary"
                className={panel.dangerOutline}
                onClick={() => ask('remove')}
              >
                <Trash2 size={15} aria-hidden="true" /> Remove carrier
              </Button>
            ) : (
              <p className={panel.dialogNote}>
                Changes are saved with Save insurance.
              </p>
            )}
            <div className={panel.footerActions}>
              <Button type="button" variant="secondary" onClick={requestClose}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={
                  Boolean(errors.name || errors.aliases) ||
                  (carrier ? !changed : false)
                }
                onClick={() => onApply(draft)}
              >
                {carrier ? 'Apply' : 'Add carrier'}
              </Button>
            </div>
          </>
        )
      }
    >
      {mode === 'remove' && carrier ? (
        <RemoveQuestion
          title={`Remove ${carrier.name}?`}
          lines={[
            'Ava stops listing it as accepted once you save insurance.',
            'Other carriers and notes aren’t affected.',
          ]}
        />
      ) : (
        <>
          <div className={panel.field}>
            <label htmlFor={`${baseId}-name`} className={panel.fieldLabel}>
              Carrier name
            </label>
            <input
              id={`${baseId}-name`}
              data-autofocus
              value={name}
              maxLength={160}
              aria-invalid={Boolean(name.trim() && errors.name)}
              aria-describedby={
                errors.name && name.trim() ? `${baseId}-name-error` : undefined
              }
              onChange={(event) => setName(event.currentTarget.value)}
            />
            {name.trim() && errors.name ? (
              <small
                id={`${baseId}-name-error`}
                className={panel.fieldError}
                role="alert"
              >
                <AlertTriangle size={14} aria-hidden="true" />
                {errors.name}
              </small>
            ) : null}
          </div>
          <div className={panel.field}>
            <label htmlFor={`${baseId}-alias`} className={panel.fieldLabel}>
              Other names patients use
            </label>
            {aliases.length ? (
              <ul className={styles.aliasChips} aria-label="Other names">
                {aliases.map((item) => (
                  <li key={item}>
                    {item}
                    <button
                      type="button"
                      aria-label={`Remove ${item}`}
                      onClick={() =>
                        setAliases(aliases.filter((value) => value !== item))
                      }
                    >
                      <X size={13} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className={styles.aliasInput}>
              <input
                id={`${baseId}-alias`}
                value={alias}
                maxLength={100}
                placeholder="For example: Delta"
                aria-invalid={Boolean(pendingAliasError)}
                aria-describedby={`${baseId}-alias-hint${pendingAliasError ? ` ${baseId}-alias-error` : ''}`}
                onChange={(event) => setAlias(event.currentTarget.value)}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addAlias()
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={!alias.trim() || Boolean(pendingAliasError)}
                onClick={addAlias}
              >
                Add
              </Button>
            </div>
            <small id={`${baseId}-alias-hint`} className={panel.hint}>
              Abbreviations or plan names, up to 12. Press Enter to add.
            </small>
            {pendingAliasError ? (
              <small
                id={`${baseId}-alias-error`}
                className={panel.fieldError}
                role="alert"
              >
                <AlertTriangle size={14} aria-hidden="true" />
                {pendingAliasError}
              </small>
            ) : null}
            {shared.length ? (
              <small className={styles.softWarning}>
                <AlertTriangle size={14} aria-hidden="true" />
                {shared.join(', ')} already {shared.length > 1 ? 'use' : 'uses'}{' '}
                one of these names. When a patient says it, Ava can’t tell which
                carrier they mean.
              </small>
            ) : null}
          </div>
        </>
      )}
    </DialogFrame>
  )
}

function NoteDialog({
  note,
  others,
  onApply,
  onRemove,
  onClose,
}: {
  note?: string
  others: string[]
  onApply: (note: string) => void
  onRemove?: () => void
  onClose: () => void
}) {
  const baseId = useId()
  const { mode, setMode, ask, safeChoice } = useAsk()
  const [value, setValue] = useState(note ?? '')
  const trimmed = value.trim()
  const error = noteError(trimmed, others)
  const changed = trimmed !== (note ?? '').trim()

  function requestClose() {
    if (changed && trimmed) ask('discard')
    else onClose()
  }

  return (
    <DialogFrame
      eyebrow="Insurance notes"
      title={note === undefined ? 'Add note' : 'Edit note'}
      onClose={() => (mode === 'edit' ? requestClose() : setMode('edit'))}
      footer={
        mode === 'remove' ? (
          <div className={panel.footerActions}>
            <Button
              ref={safeChoice}
              type="button"
              variant="secondary"
              onClick={() => setMode('edit')}
            >
              Keep note
            </Button>
            <Button
              type="button"
              className={panel.dangerSolid}
              onClick={onRemove}
            >
              Remove note
            </Button>
          </div>
        ) : mode === 'discard' ? (
          <>
            <p className={panel.dialogNote} role="alert">
              Discard your changes to this note?
            </p>
            <div className={panel.footerActions}>
              <Button
                ref={safeChoice}
                type="button"
                variant="secondary"
                onClick={() => setMode('edit')}
              >
                Keep editing
              </Button>
              <Button type="button" onClick={onClose}>
                Discard changes
              </Button>
            </div>
          </>
        ) : (
          <>
            {onRemove ? (
              <Button
                type="button"
                variant="secondary"
                className={panel.dangerOutline}
                onClick={() => ask('remove')}
              >
                <Trash2 size={15} aria-hidden="true" /> Remove note
              </Button>
            ) : (
              <p className={panel.dialogNote}>
                Changes are saved with Save insurance.
              </p>
            )}
            <div className={panel.footerActions}>
              <Button type="button" variant="secondary" onClick={requestClose}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={Boolean(error) || (note !== undefined && !changed)}
                onClick={() => onApply(trimmed)}
              >
                {note === undefined ? 'Add note' : 'Apply'}
              </Button>
            </div>
          </>
        )
      }
    >
      {mode === 'remove' && note !== undefined ? (
        <RemoveQuestion
          title="Remove this note?"
          lines={[
            'Ava stops using it once you save insurance.',
            'Accepted carriers and other notes aren’t affected.',
          ]}
        />
      ) : (
        <div className={panel.field}>
          <label htmlFor={`${baseId}-note`} className={panel.fieldLabel}>
            Note
          </label>
          <textarea
            id={`${baseId}-note`}
            className={styles.ruleInput}
            data-autofocus
            rows={4}
            value={value}
            aria-invalid={Boolean(trimmed && error)}
            aria-describedby={`${baseId}-note-hint${trimmed && error ? ` ${baseId}-note-error` : ''}`}
            onChange={(event) => setValue(event.currentTarget.value)}
          />
          <small id={`${baseId}-note-hint`} className={styles.fieldFoot}>
            <span>One fact or policy per note.</span>
            <span
              data-over={
                trimmed.length > INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS ||
                undefined
              }
            >
              {trimmed.length}/{INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS}
            </span>
          </small>
          {trimmed && error ? (
            <small
              id={`${baseId}-note-error`}
              className={panel.fieldError}
              role="alert"
            >
              <AlertTriangle size={14} aria-hidden="true" />
              {error}
            </small>
          ) : null}
        </div>
      )}
    </DialogFrame>
  )
}
